import { api } from './api';
import { idb } from './idb';
import { identityAnnotations } from './identityAnnotations';
import { submitStep } from './stepSubmit';
import { track } from './analytics';
export { drafts } from './drafts';
export type { IdentityAnnotationField, IdentityAnnotationSource, IdentityAnnotations } from './identityAnnotations';

export interface Application {
  id: number | string;
  /** Internal Globus readiness state (staff triage signal). Used only for
   *  terminal filtering — never displayed to the applicant. */
  status: string;
  /** Raw journey-stage key (e.g. "application", "visa_sevis"). */
  journeyPosition?: string;
  /** Applicant-facing journey-stage label (e.g. "Intake", "Visa / SEVIS") —
   *  what the profile shows as the application status. */
  journeyPositionLabel?: string | null;
  /** Operator-friendly application reference (GLB-A-#####). */
  shortCode?: string;
  programId?: string;
  /** Human-readable program code (e.g. "CAMP_WP"), distinct from `programId`
   *  (Globus's internal per-environment program UUID). */
  programCode?: string;
  programName?: string;
  brandName?: string;
  firstName?: string;
  lastName?: string;
  preferredName?: string;
  email?: string;
  metadata?: Record<string, unknown>;
  /** Stage-definition-derived terminal flag — the authoritative "this
   *  application no longer drives the portal" signal. */
  stageIsTerminal?: boolean;
  appUpdatedAt?: string;
}

export interface ResolvedRef {
  id: string;
  name?: string;
}

/**
 * Bootstrap context resolved ONCE by the BFF (/me/context): the applicant's
 * program, brand and season/operational cycle. Every downstream fetch is
 * driven off these ids — never a hardcoded or defaulted value. `unresolved`
 * lists parts that should have resolved but didn't; consumers must surface
 * that state instead of substituting a default.
 */
export interface PortalContextData {
  applicantId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  /** Applicant-chosen display name (optional) — the SPA prefers this over
   *  `firstName` for display (sidebar/header) when set. Never used for
   *  legal-name surfaces (SEVIS, DS-2019, Terms signature). */
  preferredName?: string;
  /** Current account phone — display source for the Profile page's Contact
   *  details card; writes go via PATCH /me/profile. Typically Hub-canonical
   *  E.164, but Path-1 step submissions can write non-canonical values, so
   *  it may carry legacy formatting (spaces/dashes) — normalize before
   *  comparing against user input. */
  phone?: string;
  /** From the BFF: its photo-stream path when an account photo exists, else
   *  null. The provider swaps it for a blob object URL before consumers see
   *  it (the stream needs a Bearer header <img src> can't send). */
  profileImageUrl?: string | null;
  hasApplication: boolean;
  applicationId: string | null;
  applicationStatus: string | null;
  program: (ResolvedRef & { key?: string }) | null;
  brand: (ResolvedRef & { slug?: string }) | null;
  season: ResolvedRef | null;
  unresolved: Array<'program' | 'brand' | 'season'>;
}

export interface BrandTheme {
  id?: string;
  slug?: string;
  name?: string;
  logoUrl?: string | null;
  colors?: Record<string, string> | null;
  portalTheme?: Record<string, string> | null;
}

/**
 * Step-access contract record — the server's single authoritative decision
 * for one step of one application. Consumed by the portal exclusively from
 * `GET /api/portal/applications/:applicationId/step-access`; the portal never
 * derives access level or lock state itself (Nexus section-locking contract).
 */
export interface StepAccessEntry {
  stepKey: string;
  /** Server-resolved access level. `hidden` steps must be omitted from
   *  navigation entirely; `locked` and `view_only` render read-only. */
  accessLevel: 'editable' | 'view_only' | 'locked' | 'hidden';
  /** Machine-readable reason code (e.g. `STEP_ALREADY_SUBMITTED`,
   *  `MANUAL_SECTION_LOCK`, `TIME_WINDOW_LOCK`). Informational only for the
   *  portal — never used to derive behaviour. */
  reason?: string;
  /** Staff-authored applicant-facing banner text. Present only when
   *  `accessLevel === 'locked'` AND the lock carries an authored message.
   *  Rendered verbatim — never modified or translated by the portal. */
  bannerMessage?: string;
  /** ISO UTC timestamp of the scheduled auto-release for time-window locks.
   *  The portal displays this in the applicant's local timezone; it never
   *  computes whether the lock has expired from this value. */
  unlock_at?: string;
  needsAction?: boolean;
  /** Applicant-facing upstream rejection fields. The message is rendered
   * verbatim; the code is metadata and must never be expanded to reviewer copy. */
  rejectionMessage?: string;
  rejectionCode?: string;
  documentReviewStatus?: string;
  /** Authoritative applicant capabilities. Optional only for compatibility
   * with snapshots produced before Globus exposed these fields. */
  canApplicantEdit?: boolean;
  canApplicantSubmit?: boolean;
  unlockConditions?: unknown;
  reviewItems?: StepReviewItem[];
  reviewFeedback?: {
    decision?: string;
    overallComment?: string | null;
    items?: ReviewFeedbackItem[];
  } | null;
}

/** Applicant-safe compliance evaluation projected by the portal BFF. */
export interface ComplianceFeedback {
  reasonCode: string;
  message: string;
  evaluatedAt: string;
  freshness: 'fresh' | 'stale';
}

/** Applicant-visible field review item attached to step access. */
export interface StepReviewItem {
  id: string;
  targetType: string;
  targetItemId: string | null;
  targetLabel: string | null;
  fieldPath: string | null;
  status: string;
  comment: string | null;
  requestedAt?: string;
}

export interface StepItem {
  stepKey: string;
  title?: string;
  name?: string;
  phaseKey?: string;
  phaseName?: string;
  /** Canonical Globus journey position — drives the 3-phase bucket mapping.
   *  Null when Globus has not authored it (see NormalStep.journeyPositionKey). */
  journeyPositionKey?: string | null;
  status?: string;
  globusMessage?: string;
  stepConfig?: unknown;
  stepData?: Record<string, unknown>;
  [k: string]: unknown;
}

/**
 * Application-level rejection envelope rendered as the dashboard alert card.
 * Rides on the APPLICATION row itself (`application.metadata.rejection`, with
 * a legacy bare top-level `rejection` key on older rows) — distinct from
 * NotificationReasonMetadata below, which rides on step_rejected NOTIFICATION
 * rows and guarantees the reasonCode+message pairing invariant. This envelope
 * has no pairing guarantee: every field is optional. The render has tolerated
 * both spellings (`code`/`reason` alongside `reasonCode`/`message`) since the
 * original dashboard envelope work; whether producer-side data ever actually
 * emitted the alternate spellings is unverified — they are handled defensively,
 * not from confirmed data history. Render codes and messages verbatim.
 */
export interface RejectionEnvelope {
  /** Stable machine reason code (e.g. "INCOMPLETE_DOCUMENTATION"). */
  reasonCode?: string;
  /** Alternate spelling of `reasonCode` the render tolerates defensively
   *  (presence in real data unverified). */
  code?: string;
  /** Curated applicant-facing message. Render verbatim; never re-template. */
  message?: string;
  /** Alternate spelling of `message` the render tolerates defensively
   *  (presence in real data unverified). */
  reason?: string;
}

/**
 * Curated reason-code envelope carried on step_rejected notifications.
 * When present, both keys are always populated (pairing invariant).
 * Null for legacy notifications created before the producer change, or for
 * rare decisions with no resolvable reason code.
 */
export interface NotificationReasonMetadata {
  /** Stable machine code (e.g. "INCOMPLETE_DOCUMENTATION"). Use for
   *  deep-linking and analytics; never display raw to the applicant. */
  reasonCode: string;
  /** Bare curated applicant-safe reason text — no sentence template applied.
   *  Render verbatim; never re-template client-side. */
  message: string;
}

/**
 * A single notification row from GET /api/portal/notifications.
 * The shape is stable across all notification types; only step_rejected rows
 * carry a non-null metadata envelope.
 */
export interface PortalNotification {
  id: string;
  type: string;
  title: string;
  /** Full templated sentence (e.g. "Your … submission has been rejected.
   *  Reason: …"). Render verbatim; never re-template. */
  message: string;
  severity: 'info' | 'warning' | 'error' | 'success';
  /** Server-owned grouping decision used by the notification inbox. */
  requiresAction: boolean;
  /** Entity the notification links to (e.g. "application"). */
  linkEntityType?: string | null;
  /** ID of the linked entity (e.g. the applicationId). */
  linkEntityId?: string | null;
  /** Curated reason envelope — present only on step_rejected rows produced
   *  after the Globus producer change. Null for legacy rows. */
  metadata: NotificationReasonMetadata | null;
  read: boolean;
  createdAt: string;
}

export interface NotificationsPage {
  notifications: PortalNotification[];
  total: number;
  page: number;
  pageSize: number;
}

export interface NotificationsCount {
  unread: number;
}

/**
 * One renewal/extension flow-availability entry from the BFF's
 * GET /applications/:id/available-flows passthrough (BOP Task #775).
 * Eligibility is decided entirely by Globus — the portal reflects, never
 * computes (AP-8 rule).
 */
export interface AvailableFlowEntry {
  applicationType: 'renewal' | 'extension';
  eligible: boolean;
  /** ISO timestamp the flow became available, when Globus knows it. */
  eligibleSince: string | null;
  /** Applicant-safe explanation when ineligible. Available but UNRENDERED
   *  today — the card shows eligible flows only; this is reserved for a
   *  future ineligible-state display (a leak test guards that the string
   *  never reaches the DOM until then). */
  reason: string | null;
  workflowDefinitionId: string | null;
  /** The season the typed create MUST target for this flow, echoed by Globus.
   *  `null` when no target season resolves (a renewal whose program has no
   *  upcoming season configured) — the flow is then not creatable. The BFF
   *  reads this server-side; the client never sends a seasonId. */
  targetSeasonId?: string | null;
}

export interface AvailableFlowsResponse {
  applicationId: string;
  flows: AvailableFlowEntry[];
}

/** Globus's 201 envelope for a renewal/extension create. */
export interface CreatedFlowApplication {
  applicationId: string;
  workflowDefinitionId: string | null;
}

/**
 * A single item from the Globus review-feedback envelope.
 * targetItemId is authoritative for matching to composite items (skills/hobbies).
 * targetType distinguishes 'skill' vs 'hobby' entries.
 * fieldKey narrows to a specific field within the item (e.g. 'experience_notes').
 */
export interface ReviewFeedbackItem {
  id: string;
  targetItemId: string | null;
  targetType: string;          // 'skill' | 'hobby'
  fieldKey: string | null;     // e.g. 'experience_notes'
  /** Rich review_items decorations retained for display/fallback. Identity
   * matching must still use targetItemId, never fieldPath's array index. */
  targetLabel?: string | null;
  fieldPath?: string | null;
  status: string;              // 'changes_requested' | 'applicant_resubmitted'
  comment: string | null;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * The review-feedback envelope for a single step.
 * `items` is the curated list; `overallComment` is the step-level banner text.
 */
export interface ReviewFeedback {
  stepKey: string;
  overallComment: string | null;
  items: ReviewFeedbackItem[];
}

const passportNameSyncPath = (id: string | number) =>
  ['/applications', encodeURIComponent(String(id)), 'passport-name-sync'].join('/');

const referenceMutationPath = (
  applicationId: string | number,
  submissionId: string,
  action: 'replace' | 'resend',
) =>
  [
    '/applications',
    encodeURIComponent(String(applicationId)),
    'references',
    encodeURIComponent(submissionId),
    action,
  ].join('/');

export const portal = {
  context: () => api<PortalContextData>('/me/context'),
  brandTheme: (brandId: string) => api<BrandTheme>(`/brands/${encodeURIComponent(brandId)}/theme`),
  programConfig: (programId: string) => api<any>(`/programs/${encodeURIComponent(programId)}/config`),
  checkinsSeason: (seasonId: string) => api<any>(`/checkins/recurring/season/${encodeURIComponent(seasonId)}`),
  checkinsCurrentSeason: () => api<any>('/checkins/recurring/current-season'),
  checkinTemplate: (programId: string) => api<any>(`/checkins/templates/${encodeURIComponent(programId)}`),
  applications: () => api<Application[]>('/applications'),
  application: (id: string | number) => api<Record<string, any>>(`/applications/${id}`),
  steps: (id: string | number) => api<any>(`/applications/${id}/steps`),
  /** Lean journey projection (no per-step fieldsConfig/stepData) — ~a few KB vs
   *  ~170KB for steps(). For progress/journey/check-in views that only read
   *  titles, phases and completion state. The form page uses steps(). */
  journey: (id: string | number) => api<any>(`/applications/${id}/journey`),
  /** One step's full row (stepConfig + stepData, ~10-30KB). WRITE-BEARING for
   *  agreement steps: the server pins the agreement version on first serve
   *  (canonical explanation: GlobusService.getApplicationStepConfig). NEVER
   *  prefetch this route — fetch it on-demand for the single selected step
   *  only (useHydratedStep). */
  step: (id: string | number, stepKey: string) =>
    api<any>(`/applications/${id}/steps/${encodeURIComponent(stepKey)}`),
  programs: () => api<any[]>('/programs'),
  createApplication: (programId: string) =>
    api<any>('/applications', {
      method: 'POST',
      body: JSON.stringify({ programId, requestedFlowType: 'standard', sourceContext: 'participant-portal' }),
    }),
  submitStep,
  identityAnnotations,
  /** Partial (draft) save — no validation, no completion. Backs the autosave
   *  indicator: only a resolved call means the work is durably in Globus. */
  saveDraft: (id: string | number, stepKey: string, responses: unknown) =>
    api<any>(`/applications/${id}/steps/${encodeURIComponent(stepKey)}/draft`, {
      method: 'POST',
      body: JSON.stringify({ responses }),
    }),
  documents: (id: string | number) => api<any[]>(`/applications/${id}/documents`),
  /** Criminal-background translation pipeline status (Hub PR #1797, proxied
   *  by the BFF). Returns `{ applicationId, packages: [...] }` — each package
   *  keyed by stepKey+fieldKey with processingStatus and original/translation
   *  artifact refs. Poll after a background-check step submit. */
  criminalBackgroundStatus: (id: string | number) =>
    api<{ applicationId: string; packages: unknown[] }>(`/applications/${id}/criminal-background/status`, {
      // Polling contract: pair with the BFF's Cache-Control: no-store so no
      // browser/HTTP cache can replay a stale package list between polls.
      cache: 'no-store',
    }),
  /** SWT enrollment-proof pipeline status (Hub Task #11, proxied by the BFF).
   *  `assessment` is null until the Proof of Student Status upload triggers
   *  the Hub pipeline. Poll after a proof-of-student-status step submit. */
  swtEnrollmentProofStatus: (id: string | number) =>
    api<{ applicationId: string; assessment: unknown }>(`/applications/${id}/swt/enrollment-proof/status`),
  /** SWT academic (professor/university) verification status (Hub Task #12,
   *  proxied by the BFF). Read-only: creation is staff-driven by design. */
  swtAcademicVerificationStatus: (id: string | number) =>
    api<{ applicationId: string; vacationVerification: unknown; academicVerificationRequests: unknown[] }>(
      `/applications/${id}/swt/academic-verification/status`,
    ),
  /** Delete (withdraw) a not-yet-approved document from storage. Globus rejects
   *  deleting an approved doc (409); the engine only calls this for removable
   *  fields, and it's best-effort on the client side. */
  deleteDocument: (id: string | number, docId: string) =>
    api<any>(`/applications/${id}/documents/${encodeURIComponent(docId)}`, { method: 'DELETE' }),
  /** Reference Details step: sanitized reference rows + referee management.
   *  Globus owns every state machine (dupe/max-slot guards, revoke races,
   *  resend cooldowns); the SPA renders statuses and relays actions. */
  references: (id: string | number) => api<ReferenceRow[]>(`/applications/${id}/references`),
  createReference: (id: string | number, referee: RefereeDetails) =>
    api<ReferenceRow>(`/applications/${id}/references`, { method: 'POST', body: JSON.stringify(referee) }),
  replaceReference: (applicationId: string | number, submissionId: string, referee: RefereeDetails) =>
    api<{ revoked: ReferenceRow; created: ReferenceRow }>(referenceMutationPath(applicationId, submissionId, 'replace'), {
      method: 'POST',
      body: JSON.stringify(referee),
    }),
  resendReference: (applicationId: string | number, submissionId: string) =>
    api<ReferenceRow>(referenceMutationPath(applicationId, submissionId, 'resend'), { method: 'POST' }),
  /**
   * Sponsor/program-team review feedback for a specific application step.
   * Returns the curated envelope (overallComment + per-item comments) so the
   * portal can surface inline feedback on the relevant composite fields.
   * "No feedback" is an EMPTY envelope ({ overallComment: null, items: [] }),
   * never a 404 (api() throws plain Errors without a status property) and
   * never a null body (unparseable by res.json()).
   */
  reviewFeedback: (appId: string | number, stepKey: string) =>
    api<ReviewFeedback | null>(
      `/applications/${appId}/steps/${encodeURIComponent(stepKey)}/review-feedback`
    ),
  /** Step-access contract: the server's single authoritative access decision
   *  for every step of this application. Fetched alongside application load
   *  and re-fetched after any step submission or on navigation/focus events.
   *  The portal is a pure renderer of this contract — it never derives lock
   *  state from workflow, journey, or policy data. */
  stepAccess: (id: string | number) => api<StepAccessEntry[]>(`/applications/${id}/step-access`),
  /** Renewal/extension eligibility for the dashboard card — Globus-owned
   *  decisions, passed through the BFF verbatim. */
  availableFlows: (id: string | number) => api<AvailableFlowsResponse>(`/applications/${id}/available-flows`),
  /** Start a renewal/extension flow. `id` MUST be the application
   *  availableFlows() was queried for — the BFF uses it as both the
   *  eligibility anchor and priorApplicationId. No body: applicantId,
   *  programId and seasonId are all resolved server-side from the token and
   *  Globus (see ApplicationsController.createFlow). */
  createFlowApplication: (id: string | number, applicationType: 'renewal' | 'extension') =>
    api<CreatedFlowApplication>(`/applications/${id}/flows/${applicationType}`, { method: 'POST' }),
  /** Apply an OCR-issued, opaque passport-name proof. The proof is the only
   * body value: names must never be reconstructed or sent by the browser. */
  syncPassportName: (id: string | number, proof: string) =>
    api<{ updated: boolean }>(passportNameSyncPath(id), {
      method: 'POST',
      body: JSON.stringify({ proof }),
    }),
  /** Outstanding portal_access agreements for the signed-in applicant — backs
   *  the first-login blocking agreement modal. Empty list = account current. */
  requiredAgreements: () => api<RequiredAgreementsResponse>('/agreements/required'),
  /** Record acceptance of the EXACT current version. `typedName` is only sent
   *  when the agreement's signatureMethod is "typed_name". */
  acceptAgreement: (agreementId: string, versionId: string, typedName?: string) =>
    api<AcceptAgreementResult>('/agreements/acceptances', {
      method: 'POST',
      body: JSON.stringify({ agreementId, versionId, typedName }),
    }),

  /* ---------- notifications (AP-13) ---------- */

  /** Paged notification list. page is 1-based; pageSize defaults to 20. */
  notifications: (page = 1, pageSize = 20) =>
    api<NotificationsPage>(`/notifications?page=${page}&pageSize=${pageSize}`),
  /** Unread notification count — lightweight poll for the bell badge. */
  notificationsCount: () => api<NotificationsCount>('/notifications/count'),
  /** Mark a single notification as read. */
  markNotificationRead: (id: string) =>
    api<void>(`/notifications/${encodeURIComponent(id)}/read`, { method: 'PATCH' }),
  /** Mark all notifications as read. */
  markAllNotificationsRead: () =>
    api<void>('/notifications/read-all', { method: 'POST' }),
  /** Embassy/consulate reference lookup — options for the VISA step's
   *  embassy_city field. */
  embassies: (params: Record<string, string | number | undefined> = {}) => {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');
    return api<EmbassyConsulate[]>(`/embassies${qs ? `?${qs}` : ''}`);
  },
  /* ---------- arrival information (Task #1673) ---------- */
  /** Post-placement arrival information for the signed-in applicant.
   *  Returns the flat dual-state response — discriminate on `available`
   *  before accessing placement-specific fields. Never 404. */
  arrivalInformation: () => api<ArrivalInformationResult>('/arrival-information'),
};

/** Globus embassy/consulate record (portal reference data). */
export interface EmbassyConsulate {
  id: number;
  city: string | null;
  country: string | null;
  operatorCountry?: string;
  operatorCountryIso?: string | null;
  hostCountry?: string;
  hostCountryIso?: string | null;
  type?: string;
}

/**
 * Shape Globus embassy records into form-engine options for the VISA step's
 * `embassy_city` searchable field. The engine auto-fills `embassy_country`
 * from the selected option's `country` key (matching on String(value), so a
 * numeric id is a valid value).
 */
export function toEmbassyOptions(records: EmbassyConsulate[]): Array<{ value: string; label: string; country: string }> {
  return records
    .map((r) => {
      const city = r.city ?? r.hostCountry ?? '';
      const country = r.country ?? r.hostCountry ?? '';
      return {
        value: String(r.id),
        label: country && city && country !== city ? `${city}, ${country}` : city || country,
        country,
      };
    })
    .filter((o) => o.label)
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Deep-clone a step config, binding `options` onto every `embassy_city` field
 * (the engine renders options verbatim — it never fetches them itself).
 */
export function injectEmbassyOptions<T>(node: T, options: Array<{ value: string; label: string; country: string }>): T {
  if (Array.isArray(node)) return node.map((n) => injectEmbassyOptions(n, options)) as unknown as T;
  if (node && typeof node === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(node as Record<string, unknown>)) out[k] = injectEmbassyOptions(v, options);
    const f = node as { name?: string; id?: string };
    if (f.name === 'embassy_city' || f.id === 'embassy_city') out.options = options;
    return out as T;
  }
  return node;
}

// All referee fields are required end-to-end (product decision 2026-07-07).
export interface RefereeDetails {
  refereeName: string;
  refereeEmail: string;
  refereeRelationship: string;
  refereeRelationshipDetail?: string;
  refereeOrganization: string;
  refereeJobTitle: string;
  refereePhone: string;
  knownSixMonths: boolean;
}

export interface ReferenceRow {
  id: string;
  applicationId: string;
  refereeName: string;
  refereeEmail: string;
  refereeRelationship: string | null;
  refereeRelationshipDetail: string | null;
  refereeOrganization: string | null;
  refereeJobTitle: string | null;
  refereePhone: string | null;
  referenceType: string;
  status: 'invited' | 'reminded' | 'completed' | 'expired' | 'revoked';
  reviewStatus: 'pending_review' | 'approved' | 'rejected' | null;
  reviewNotes: string | null;
  invitedAt: string;
  emailSentAt: string | null;
  lastReminderAt: string | null;
  reminderCount: number;
  expiresAt: string;
  completedAt: string | null;
  revokedAt: string | null;
}

/** How the applicant signs an agreement: a checkbox, or a typed full name. */
export type AgreementSignatureMethod = 'checkbox' | 'typed_name';

/** One outstanding portal_access agreement (Task #1671). */
export interface RequiredAgreement {
  agreementId: string;
  agreementCode: string;
  agreementName: string;
  assignmentId: string;
  versionId: string;
  versionLabel: string;
  effectiveDate: string | null;
  contentFormat: string;
  /** Editor-authored HTML — sanitize before rendering (TermsGateModal). */
  contentHtml: string | null;
  contentHash: string;
  isRequired: boolean;
  signatureMethod: AgreementSignatureMethod;
  /** Optional agreements may be declined; required ones block the portal. */
  allowDecline: boolean;
  allowDownload: boolean;
}

export interface RequiredAgreementsResponse {
  agreements: RequiredAgreement[];
  total: number;
}

export interface AcceptAgreementResult {
  accepted: boolean;
  alreadyAccepted: boolean;
  acceptanceId: string | null;
  agreementId: string;
  versionId: string;
  acceptedAt?: string;
  contentHash?: string;
  agreements: RequiredAgreement[];
  total: number;
}

/** Humanize machine enums for display (on_track → "On track"). Never applied
 * to Globus applicant-facing messages, which render verbatim (AP-1). */
export function humanize(value?: string): string {
  if (!value) return '—';
  const s = value.replace(/[_-]+/g, ' ').trim().toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Authenticated fetch of a document → object URL + its MIME type. The blob
 * carries the response Content-Type, so an <img>/<iframe> pointed at the object
 * URL renders it inline regardless of the upstream `attachment` disposition.
 * Caller owns the URL and must URL.revokeObjectURL it when done. */
export async function fetchDocumentObject(path: string): Promise<{ url: string; type: string }> {
  const { auth } = await import('./api');
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(`${base}${path}`, { headers: { Authorization: `Bearer ${auth.getToken()}` } });
  if (!res.ok) throw new Error(`document fetch failed: ${res.status}`);
  const blob = await res.blob();
  // A document path the BFF doesn't serve (e.g. the Hub-shaped
  // /api/documents/... it writes into step_data) falls through Vite's proxy to
  // the SPA itself, which "succeeds" with index.html — previously rendered as
  // a silently blank preview. Fail loudly so the previewer shows its
  // unavailable state instead.
  if (blob.type.includes('text/html')) throw new Error(`document fetch returned HTML, not a document: ${path}`);
  return { url: URL.createObjectURL(blob), type: blob.type };
}

/** Why passport extraction failed — drives the `passport_extraction_failed`
 * analytics event. `client_error` is fired by the page's catch around the
 * extraction factory, not by extractPassport itself. Note: "OCR found nothing"
 * is NOT a failure reason — it surfaces as success with zero applied fields
 * and is tracked separately as `passport_extraction_empty`. */
export type PassportExtractionFailureReason =
  | 'http_error'
  | 'empty_body'
  | 'parse_error'
  | 'network'
  | 'client_error';

export type PassportExtractionResult =
  | { ok: true; data: Record<string, unknown> | null; nameSyncProof?: string }
  | { ok: false; reason: PassportExtractionFailureReason; status?: number };

// ── Arrival Information (Task #1673) ─────────────────────────────────────────

/**
 * Flat applicant-facing arrival information returned by the BFF
 * `GET /v1/arrival-information` when the applicant has a confirmed or active
 * placement. Field names mirror the Globus SDK `PortalArrivalInformation`
 * contract exactly (Task #1673).
 *
 * Conditional-field rules:
 *   - `transportPickUpCost` is only non-null when `pickUpProvided` is true.
 *   - `contactPhoneNumber` is only non-null when `shouldContactBeforeArrival`
 *     is true.
 */
export interface ArrivalInformation {
  available: true;
  hostName: string | null;
  portOfEntryAirport: string | null;
  nearestAirportToJobsite: string | null;
  returnToUponArrival: string | null;
  pickUpProvided: boolean;
  /** Non-null only when `pickUpProvided` is true. */
  transportPickUpCost: string | null;
  shouldContactBeforeArrival: boolean;
  contactName: string | null;
  contactTitle: string | null;
  contactEmail: string | null;
  /** Non-null only when `shouldContactBeforeArrival` is true. */
  contactPhoneNumber: string | null;
  /** ISO weekday numbers (1 = Monday ... 7 = Sunday), ascending. Empty when
   *  no preferred days are configured. */
  preferredArrivalDays: number[];
  preferredArrivalTimes: string | null;
  reportingTo: string | null;
  generalArrivalInstructions: string | null;
}

/** Returned before a confirmed placement exists. */
export interface ArrivalInformationUnavailable {
  available: false;
  reason: 'NO_CONFIRMED_PLACEMENT';
}

/** Discriminated union — always check `available` before reading placement fields. */
export type ArrivalInformationResult = ArrivalInformation | ArrivalInformationUnavailable;

/**
 * Passport OCR extraction transport (AP-7). Hands the uploaded file's reference
 * URL (the engine's `result.url` download route) to the BFF, which streams the
 * bytes to the internal OCR service and returns the FLAT extracted fields the
 * form-engine passport factory maps onto the step. Never throws: every failure
 * (OCR disabled/unreachable, HTTP error, empty MRZ body, network drop) returns
 * a discriminated `{ ok: false }` result — and fires
 * `passport_extraction_failed` — so the caller can tell failure apart from
 * "no data" instead of silently leaving the fields blank.
 */
export async function extractPassport(
  fileUrl: string,
  fileName?: string,
  applicationId?: string | number,
  stepKey?: string,
): Promise<PassportExtractionResult> {
  const fail = (reason: PassportExtractionFailureReason, status?: number): PassportExtractionResult => {
    track('passport_extraction_failed', { reason, status });
    return { ok: false, reason, status };
  };
  const { auth } = await import('./api');
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  let res: Response;
  let text: string;
  try {
    res = await fetch(`${base}/api/v1/ai/documents/extract-passport`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth.getToken()}` },
      body: JSON.stringify({ fileUrl, fileName, applicationId, stepKey }),
    });
    if (!res.ok) return fail('http_error', res.status);
    text = await res.text();
  } catch {
    return fail('network');
  }
  if (!text) return fail('empty_body', res.status);
  try {
    const data = JSON.parse(text) as Record<string, unknown> | null;
    // The OCR route remains flat for existing consumers. Newer responses append
    // an opaque proof; expose it separately while retaining the old `data` shape.
    const nameSyncProof = data && typeof data.nameSyncProof === 'string' ? data.nameSyncProof : undefined;
    return { ok: true, data, ...(nameSyncProof ? { nameSyncProof } : {}) };
  } catch {
    return fail('parse_error', res.status);
  }
}

/** Authenticated document download: fetch with Bearer → blob → save. */
export async function downloadDocument(path: string, fileName: string): Promise<void> {
  const { url } = await fetchDocumentObject(path);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

/* ---------- document upload with client-side image compression (AP-7) ---------- */
export async function compressImage(file: File, maxDim = 2000, quality = 0.82): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') return file;
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob: Blob = await new Promise((r) => canvas.toBlob((b) => r(b!), 'image/jpeg', quality));
  return new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' });
}

export async function uploadDocument(
  appId: string | number,
  file: File,
  meta: { documentType?: string; stepKey?: string; fieldName?: string } = {},
): Promise<any> {
  const compressed = await compressImage(file);
  const form = new FormData();
  form.append('file', compressed);
  if (meta.documentType) form.append('documentType', meta.documentType);
  if (meta.stepKey) form.append('stepKey', meta.stepKey);
  if (meta.fieldName) form.append('fieldName', meta.fieldName);
  const { auth } = await import('./api');
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(`${base}/api/v1/applications/${appId}/documents`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${auth.getToken()}` },
    body: form,
  });
  if (!res.ok) throw new Error(`upload failed: ${res.status}`);
  track('document_uploaded', { documentType: meta.documentType, size: compressed.size });
  return res.json().catch(() => {
    throw new Error('The upload response could not be read. Please check the uploaded file before continuing.');
  });
}

/**
 * Unsupported-language CBC fallback upload (Asana 1217709826504784): the
 * applicant's own English version of a background-check document, once the
 * Hub has fenced the record into `awaiting_applicant_translation` (detected
 * language outside the AI-translation allowlist — see cbcTranslation.ts's
 * `translationFallbackByField`). `requirementId` comes from that projection,
 * not the application id. The Hub 409s with TRANSLATION_FALLBACK_NOT_AVAILABLE
 * if the record has moved on since the poll that surfaced the fallback UI.
 */
export async function uploadCbcApplicantTranslation(
  requirementId: string,
  file: File,
): Promise<{ artifact: { id: string; artifactRole: string; versionNumber: number; isCurrent: boolean } }> {
  const compressed = await compressImage(file);
  const form = new FormData();
  form.append('file', compressed);
  const { auth } = await import('./api');
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(
    `${base}/api/v1/document-packages/${encodeURIComponent(requirementId)}/applicant-translation`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${auth.getToken()}` },
      body: form,
    },
  );
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as any);
    // body.message is Nest's own validation-error shape (e.g. assertValidUpload's
    // BadRequestException: {statusCode,message,error:"Bad Request"}); body.error
    // is the Hub-proxied shape ({error: "<message>", code: "..."}) once a request
    // clears BFF-level validation and reaches the Hub. Check both — a body.error
    // string alone (without checking message first) would surface Nest's generic
    // "Bad Request" reason phrase instead of the actual validation failure.
    throw new Error(
      body?.message ?? body?.error?.message ?? body?.error ?? `translation upload failed: ${res.status}`,
    );
  }
  track('cbc_applicant_translation_uploaded', { size: compressed.size });
  return res.json();
}

/* ---------- Showcase gallery photo upload (in-form media, NOT a document) ----------
 * Globus owns storage via the applicant-scoped step-media system; this posts the
 * compressed photo and returns { id, downloadUrl, fileName }. downloadUrl is a
 * canonical Globus step-media path (`/api/step-media/:id/file`) that is persisted
 * into photo_gallery and streamed back through the BFF by AuthImage. */
export async function uploadShowcaseMedia(
  appId: string | number,
  file: File,
  meta: { stepKey?: string; fieldName?: string } = {},
): Promise<{ id?: string; downloadUrl?: string; fileName?: string }> {
  const compressed = await compressImage(file);
  const form = new FormData();
  form.append('file', compressed);
  if (meta.stepKey) form.append('stepKey', meta.stepKey);
  if (meta.fieldName) form.append('fieldName', meta.fieldName);
  const { auth } = await import('./api');
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(`${base}/api/v1/applications/${appId}/showcase-photos`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${auth.getToken()}` },
    body: form,
  });
  if (!res.ok) throw new Error(`showcase upload failed: ${res.status}`);
  track('showcase_photo_uploaded', { size: compressed.size });
  return res.json().catch(() => ({}));
}

export { stepMediaIdFromUrl, deleteStepMedia, deleteStepMedia as deleteShowcaseMedia } from './stepMedia';

/* ---------- Skills & Hobbies certification upload (AP-5) ---------- */
export async function uploadSkillCertification(
  appId: string | number,
  file: File,
): Promise<{ documentId?: string; id?: string; fileName?: string }> {
  const form = new FormData();
  form.append('file', file);
  const { auth } = await import('./api');
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(`${base}/api/v1/applications/${appId}/skill-certifications`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${auth.getToken()}` },
    body: form,
  });
  if (!res.ok) throw new Error(`certification upload failed: ${res.status}`);
  track('skill_certification_uploaded', { size: file.size });
  return res.json();
}

/* ---------- profile photo (Path 2: live account update, evidence pack untouched) ---------- */
export async function uploadProfilePhoto(file: File): Promise<Record<string, unknown>> {
  const compressed = await compressImage(file, 1024, 0.85);
  const form = new FormData();
  form.append('file', compressed);
  const { auth } = await import('./api');
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(`${base}/api/v1/me/photo`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${auth.getToken()}` },
    body: form,
  });
  if (!res.ok) {
    // BFF errors are {statusCode, message} JSON (413 too large, 415 wrong
    // type, 502 storage down) — surface the message, not the raw body.
    const body = await res.text().catch(() => '');
    let msg = body;
    try {
      const parsed = JSON.parse(body);
      if (typeof parsed?.message === 'string') msg = parsed.message;
    } catch {
      /* not JSON — keep the raw text */
    }
    throw new Error(msg || `photo upload failed: ${res.status}`);
  }
  track('profile_photo_updated', { size: compressed.size });
  return res.json().catch(() => ({}));
}

/**
 * Account photo bytes → object URL. The BFF's /me/photo stream requires a
 * Bearer header, which <img src> cannot send — so the provider fetches the
 * blob and hands avatars an object URL instead. Null when no photo is stored
 * (404) or the fetch fails; callers fall back to initials.
 */
export async function fetchProfilePhotoUrl(size = 256): Promise<string | null> {
  const { auth } = await import('./api');
  const base = import.meta.env.VITE_API_BASE_URL ?? '';
  const res = await fetch(`${base}/api/v1/me/photo?s=${size}`, {
    headers: { Authorization: `Bearer ${auth.getToken()}` },
    cache: 'no-store', // reload() after an upload must show the NEW photo
  }).catch(() => null);
  if (!res || !res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/* ---------- offline-first upload queue + background sync (AP-16) ---------- */
interface QueuedUpload {
  appId: string | number;
  /** Keycloak `sub` of the user who enqueued this upload. Guards against
   *  replaying one user's queued document under a different session on a
   *  shared device (see flush). */
  ownerSub: string;
  name: string;
  type: string;
  blob: Blob;
  meta: { documentType?: string; stepKey?: string };
  queuedAt: string;
}

export const uploadQueue = {
  async enqueue(appId: string | number, file: File, meta: QueuedUpload['meta']) {
    const key = `up:${Date.now()}:${file.name}`;
    const { auth } = await import('./api');
    const entry: QueuedUpload = {
      appId,
      ownerSub: auth.getSub() ?? '',
      name: file.name,
      type: file.type,
      blob: file,
      meta,
      queuedAt: new Date().toISOString(),
    };
    await idb.set('uploads', key, entry);
    track('upload_queued_offline', { name: file.name });
    const reg = await navigator.serviceWorker?.ready.catch(() => undefined);
    // Background Sync where supported; the online-event flush covers the rest.
    await (reg as any)?.sync?.register('flush-uploads').catch(() => {});
    return key;
  },
  async flush(): Promise<number> {
    const { auth } = await import('./api');
    const currentSub = auth.getSub();
    const keys = await idb.keys('uploads');
    let sent = 0;
    for (const key of keys) {
      const q = await idb.get<QueuedUpload>('uploads', key);
      if (!q) continue;
      // Never replay a queued upload under a different user — on a shared device
      // that would attach the previous user's passport scan to this account.
      // Such an entry can never legitimately flush, so drop it.
      if (q.ownerSub && currentSub && q.ownerSub !== currentSub) {
        await idb.del('uploads', key);
        continue;
      }
      try {
        await uploadDocument(q.appId, new File([q.blob], q.name, { type: q.type }), q.meta);
        await idb.del('uploads', key);
        sent++;
      } catch {
        break; // still offline or server error — retry on next flush
      }
    }
    if (sent) track('upload_queue_flushed', { sent });
    return sent;
  },
  pending: () => idb.keys('uploads').then((k) => k.length),
};

addEventListener('online', () => void uploadQueue.flush());
navigator.serviceWorker?.addEventListener?.('message', (e) => {
  if ((e as MessageEvent).data === 'flush-uploads') void uploadQueue.flush();
});
